import { formatPhone } from "@/lib/phone";
import { useState } from "react";
import { useNavigate } from "@/lib/useNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, SelectField } from "@/components/form/Fields";
import { MultiSelect } from "@/components/form/MultiSelect";
import { TagInput } from "@/components/contacts/ContactTags";
import { Combobox } from "@/components/form/Combobox";
import { AddressFields } from "@/components/form/AddressFields";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/address";
import { checkDuplicates, DuplicateDialog } from "@/components/shared/DuplicateCheck";
import { useOptions, orgOptions, contactOptions, userOptions, quickAddContact, quickAddOrg } from "@/components/hooks/useOptions";
import { useConfig } from "@/components/hooks/useConfig";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  RELATIONSHIP_STATUS,
  RELATIONSHIP_HEALTH,
  PROPERTY_STATUS,
  PREFERRED_CONTACT,
  CONTACT_TITLES,
} from "@/lib/constants";
import { ModalNotes } from "@/components/shared/ModalNotes";
import { useOrgClassifications } from "@/components/hooks/useOrgClassifications";

const CONFIG = {
  organisation: { title: "Organisation", endpoint: "/organisations", label: "Organisation" },
  property: { title: "Property", endpoint: "/properties", label: "Property" },
  contact: { title: "Contact", endpoint: "/contacts", label: "Contact" },
};

export function EntityModal({ open, type, prefill = {}, record, onClose, onSuccess }) {
  const orgClass = useOrgClassifications();
  const cfg = CONFIG[type];
  const { users } = useAuth();
  const { orgs, contacts } = useOptions(open);
  const opts = useConfig();
  const orgDefaults = type === "organisation"
    ? { org_type: "Other", relationship_status: "Prospect", relationship_health: "Good" }
    : {};
  const [form, setForm] = useState(
    record
      ? { ...record, organisation_ids: record.organisation_ids || (record.organisation_id ? [record.organisation_id] : []) }
      : { ...orgDefaults, country: DEFAULT_COUNTRY, ...prefill, organisation_ids: prefill.organisation_ids || (prefill.organisation_id ? [prefill.organisation_id] : []) }
  );
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const navigate = useNavigate();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  if (!cfg) return null;

  const doSave = async () => {
    setSaving(true);
    try {
      const isEdit = record && record.id;
      const url = isEdit ? `${cfg.endpoint}/${record.id}` : cfg.endpoint;
      const body = { ...form };
      if (isEdit && type !== "property") delete body.notes;
      if (type === "organisation" && body.org_type === "DTR Property") {
        body.relationship_status = "N/A";
        body.relationship_health = "N/A";
        body.account_owner_id = null;
      }
      const { data } = isEdit ? await client.put(url, body) : await client.post(url, body);
      toast.success(`${cfg.title} ${isEdit ? "updated" : "created"}`);
      onSuccess && onSuccess(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!(record && record.id)) {
      const fields = type === "contact"
        ? { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, mobile: form.mobile }
        : { name: form.name };
      setSaving(true);
      const matches = await checkDuplicates(type, fields);
      setSaving(false);
      if (matches.length) { setDup(matches); return; }
    }
    await doSave();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`entity-modal-${type}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {record ? "Edit" : "New"} {cfg.title}
          </DialogTitle>
          <DialogDescription className="sr-only">{cfg.title} details form</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {type === "organisation" && (
            <>
              <TextField label="Name" required value={form.name} onChange={set("name")} testid="org-field-name" />
              <SelectField label="Organisation Type" value={form.org_type} onChange={(v) => setForm((f) => ({ ...f, org_type: v, ...(v === "DTR Property" ? { relationship_status: "N/A", relationship_health: "N/A", account_owner_id: null } : {}) }))} options={opts.org_types.filter((t) => t !== "DTR Property")} testid="org-field-type" allowEmpty={false} />
              {form.org_type === "DTR Property" ? (
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">Category &amp; Health are not applicable for DTR properties.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="Category" value={form.relationship_status} onChange={set("relationship_status")} options={RELATIONSHIP_STATUS} allowEmpty={false} />
                  <SelectField label="Relationship Health" value={form.relationship_health} onChange={set("relationship_health")} options={RELATIONSHIP_HEALTH} allowEmpty={false} />
                  <SelectField label="Market" value={form.market} onChange={set("market")} options={orgClass.markets} testid="org-field-market" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Website" value={form.website} onChange={set("website")} />
                <TextField label="Phone" value={form.phone} onChange={set("phone")} onBlur={() => set("phone")(formatPhone(form.phone))} />
              </div>
              <AddressFields value={form} onChange={patch} testid="org-address" />
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Company Name" value={form.company_name} onChange={set("company_name")} testid="org-field-company" />
                <TextField label="ABN" value={form.abn} onChange={set("abn")} testid="org-field-abn" />
              </div>
              <MultiSelect label="Linked Contacts" value={form.contact_ids || []} onChange={set("contact_ids")} options={contactOptions(contacts)} onQuickAdd={quickAddContact} testid="org-field-contacts" placeholder="Search contacts to link…" />
              <ModalNotes entityType="organisation" collection="organisations" record={record} value={form.notes} onChange={set("notes")} testid="org-field-notes" />
            </>
          )}

          {type === "property" && (
            <>
              <TextField label="Property Name" required value={form.name} onChange={set("name")} testid="property-field-name" />
              <SelectField label="Status" value={form.status} onChange={set("status")} options={PROPERTY_STATUS} allowEmpty={false} testid="property-field-status" />
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Website" value={form.website} onChange={set("website")} />
                <TextField label="Phone" value={form.phone} onChange={set("phone")} onBlur={() => set("phone")(formatPhone(form.phone))} />
              </div>
              <AddressFields value={form} onChange={patch} testid="property-address" />
              <SelectField label="Account Owner" value={form.account_owner_id} onChange={set("account_owner_id")} options={userOptions(users)} />
              <TextField label="Target Market" value={form.target_market} onChange={set("target_market")} />
              <MultiSelect label="Linked Contacts" value={form.contact_ids || []} onChange={set("contact_ids")} options={contactOptions(contacts)} testid="property-field-contacts" placeholder="Search contacts to link…" />
              <TextArea label="Notes" value={form.notes} onChange={set("notes")} />
            </>
          )}

          {type === "contact" && (
            <>
              <div className="grid grid-cols-4 gap-3">
                <SelectField label="Title" value={form.title} onChange={set("title")} options={CONTACT_TITLES} />
                <div className="col-span-3 grid grid-cols-2 gap-3">
                  <TextField label="First Name" required value={form.first_name} onChange={set("first_name")} testid="contact-field-first" />
                  <TextField label="Last Name" value={form.last_name} onChange={set("last_name")} testid="contact-field-last" />
                </div>
              </div>
              <TextField label="Job Title" value={form.job_title} onChange={set("job_title")} />
              <TagInput value={form.tags || []} onChange={set("tags")} testid="contact-field-tags" />
              <MultiSelect label="Organisations / Properties" value={form.organisation_ids || []} onChange={set("organisation_ids")} options={orgOptions(orgs)} onQuickAdd={quickAddOrg} testid="contact-field-orgs" placeholder="Search organisations & properties to link…" />
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-field-email" />
                <TextField label="Mobile" type="tel" value={form.mobile} onChange={set("mobile")} onBlur={() => set("mobile")(formatPhone(form.mobile))} testid="contact-field-mobile" />
              </div>
              <TextField label="Phone" type="tel" value={form.phone} onChange={set("phone")} onBlur={() => set("phone")(formatPhone(form.phone))} testid="contact-field-phone" />
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Country</label>
                <Combobox value={form.country || null} onChange={set("country")} options={COUNTRIES} placeholder="Country" testid="contact-field-country" />
              </div>
              <ModalNotes entityType="contact" collection="contacts" record={record} value={form.notes} onChange={set("notes")} testid="contact-field-notes" />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid={`entity-save-${type}`} className="bg-sky-600 hover:bg-sky-700">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <DuplicateDialog
        open={Boolean(dup)}
        matches={dup || []}
        entityName={cfg.label.toLowerCase()}
        onCancel={() => setDup(null)}
        onContinue={() => { setDup(null); doSave(); }}
        onOpenExisting={(m) => { setDup(null); onClose(); navigate(`${cfg.endpoint}/${m.id}`); }}
      />
    </Dialog>
  );
}
